import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  getGmailMessages,
  getGmailThreadMessages,
  getGmailSyncStatus,
  markGmailThreadRead,
  sendGmailMessage,
  startGmailSync,
  getGmailCategories,
  createLeadFromGmailThread,
  getGmailLeadThreads,
} from '../api';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Extract ?page= number from a DRF "next" URL like
 * "https://api.example.com/v1/.../messages/?page=2"
 * Returns undefined when there is no next page.
 */
function pageFromNextUrl(nextUrl) {
  if (!nextUrl) return undefined;
  try {
    const url = new URL(nextUrl);
    const p = url.searchParams.get('page');
    return p ? Number(p) : undefined;
  } catch {
    // nextUrl might be a relative path
    const match = nextUrl.match(/[?&]page=(\d+)/);
    return match ? Number(match[1]) : undefined;
  }
}

function mapThread(msg) {
  const isUnread =
    typeof msg.is_unread === 'boolean'
      ? msg.is_unread
      : typeof msg.is_read === 'boolean'
        ? !msg.is_read
        : typeof msg.unread === 'boolean'
          ? msg.unread
          : Boolean(msg.unread_count && msg.unread_count > 0);

  return {
    id: msg.id,
    thread_id: msg.thread_id,
    client_name: msg.from?.split('<')[0]?.replace(/"/g, '')?.trim() || msg.from,
    name: msg.from?.split('<')[0]?.replace(/"/g, '')?.trim() || msg.from,
    from_email: msg.from,
    to_email: msg.to,
    subject: msg.subject,
    snippet: msg.snippet,
    last_message: msg.snippet || msg.subject,
    body: msg.body,
    timestamp: msg.timestamp || msg.date || new Date().toISOString(),
    date: msg.date,
    is_unread: isUnread,
    unread_count: isUnread ? msg.unread_count || 1 : 0,
    platform: 'email',
    account_email: msg.account_email,
    is_window_active: true,
    isWindowActive: true,
    window_expires_at: null,
  };
}

// ─── Infinite conversations list ────────────────────────────────────────────

export function useInfiniteGmailConversations(
  companyId,
  params = {},
  enabled = true,
) {
  const query = useInfiniteQuery({
    queryKey: ['gmail-conversations-infinite', companyId, params],
    queryFn: ({ pageParam }) =>
      getGmailMessages(companyId, {
        ...params,
        ...(pageParam ? { page: pageParam } : {}),
      }),
    // Backend returns DRF standard {count, next, previous, results, sync}
    getNextPageParam: (lastPage) => pageFromNextUrl(lastPage?.next),
    initialPageParam: undefined,
    enabled: !!companyId && enabled,
    staleTime: 1000 * 30,
    refetchInterval: enabled ? 30000 : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const conversations = (query.data?.pages || [])
    .flatMap((page) => page?.results || [])
    .map(mapThread);

  // Sync info from the first page (same on every page)
  const syncInfo = query.data?.pages?.[0]?.sync || null;

  return {
    ...query,
    data: {
      ...query.data,
      pages: (query.data?.pages || []).map((page) => ({
        ...page,
        results: (page?.results || []).map(mapThread),
      })),
    },
    conversations,
    syncInfo,
  };
}

// ─── Thread detail messages ──────────────────────────────────────────────────

export function useCustomGmailMessages(companyId, activeMsg, enabled = true) {
  const queryClient = useQueryClient();
  const threadId =
    activeMsg?.thread_id || activeMsg?.raw?.thread_id || activeMsg?.id;

  const threadQuery = useQuery({
    queryKey: ['gmail-thread-messages', companyId, threadId],
    queryFn: () => getGmailThreadMessages({ companyId, threadId }),
    enabled: !!companyId && !!threadId && threadId !== 'undefined' && enabled,
    staleTime: 1000 * 15,
    refetchInterval: enabled ? 15000 : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  // Mark thread as read when opened
  useEffect(() => {
    if (
      enabled &&
      companyId &&
      threadId &&
      threadId !== 'undefined' &&
      activeMsg?.is_unread
    ) {
      markGmailThreadRead({ companyId, threadId })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ['gmail-conversations-infinite', companyId],
          });
        })
        .catch((e) => {
          console.error('markGmailThreadRead error:', e);
        });
    }
  }, [enabled, companyId, threadId, activeMsg?.is_unread, queryClient]);

  const threadMessages = threadQuery.data?.messages;

  const messages = useMemo(() => {
    const list = threadMessages || [];
    if (list.length > 0) {
      return list.map((m) => ({
        id: m.id,
        thread_id: m.thread_id,
        body: m.body || m.snippet || '',
        text: m.body || m.snippet || '',
        content: m.body || m.snippet || '',
        subject: m.subject,
        is_html:
          m.is_html ||
          (m.body && m.body.toLowerCase().includes('<html')) ||
          (m.snippet && m.snippet.toLowerCase().includes('<html')) ||
          false,
        timestamp: m.timestamp || m.date || new Date().toISOString(),
        direction: m.direction || 'inbound',
        sender_type: m.sender_type || 'client',
        from: m.from,
        to: m.to,
        status: 'delivered',
        platform: 'email',
      }));
    }
    if (activeMsg) {
      return [
        {
          id: activeMsg.id || activeMsg.thread_id,
          body: activeMsg.body || activeMsg.snippet || '',
          text: activeMsg.body || activeMsg.snippet || '',
          content: activeMsg.body || activeMsg.snippet || '',
          subject: activeMsg.subject,
          is_html:
            activeMsg.is_html ||
            (activeMsg.body &&
              activeMsg.body.toLowerCase().includes('<html')) ||
            (activeMsg.snippet &&
              activeMsg.snippet.toLowerCase().includes('<html')) ||
            false,
          timestamp:
            activeMsg.timestamp || activeMsg.date || new Date().toISOString(),
          direction: 'inbound',
          sender_type: 'client',
          from: activeMsg.from_email || activeMsg.client_name,
          to: activeMsg.to_email,
          status: 'delivered',
          platform: 'email',
        },
      ];
    }
    return [];
  }, [threadMessages, activeMsg]);

  return {
    messages,
    isLoadingInitial: threadQuery.isLoading,
    isFetchingOlder: false,
    hasOlder: false,
    fetchOlderMessages: () => {},
    fetchNewerMessages: () => {
      if (
        companyId &&
        threadId &&
        threadId !== 'undefined' &&
        threadId !== 'null'
      ) {
        return threadQuery.refetch();
      }
    },
  };
}

// ─── Send message ────────────────────────────────────────────────────────────

export function useSendGmailMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, payload }) =>
      sendGmailMessage({ companyId, payload }),
    onSuccess: (_, variables) => {
      toast.success('Email sent successfully via Gmail!');
      queryClient.invalidateQueries({
        queryKey: ['gmail-conversations-infinite', variables.companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['gmail-thread-messages', variables.companyId],
      });
    },
    onError: (error) => {
      const detail =
        error?.response?.data?.detail || 'Failed to send email via Gmail API.';
      toast.error(detail);
    },
  });
}

// ─── Sync controls ───────────────────────────────────────────────────────────

/**
 * Hook: query sync status every 10 s while a sync is running.
 */
export function useGmailSyncStatus(companyId, enabled = true) {
  return useQuery({
    queryKey: ['gmail-sync-status', companyId],
    queryFn: () => getGmailSyncStatus(companyId),
    enabled: !!companyId && enabled,
    staleTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.sync?.status;
      return status === 'running' ? 5000 : 30000;
    },
  });
}

/**
 * Mutation: POST .../gmail/sync/  { mode: 'all' | 'new_only' }
 */
export function useSyncGmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, mode = 'all' }) =>
      startGmailSync({ companyId, mode }),
    onSuccess: (_, variables) => {
      toast.success('Gmail sync started!');
      queryClient.invalidateQueries({
        queryKey: ['gmail-sync-status', variables.companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['gmail-conversations-infinite', variables.companyId],
      });
    },
    onError: (error) => {
      const detail =
        error?.response?.data?.detail || 'Failed to start Gmail sync.';
      toast.error(detail);
    },
  });
}

// ─── Categories ──────────────────────────────────────────────────────────────

export function useGmailCategories(companyId, enabled = true) {
  return useQuery({
    queryKey: ['gmail-categories', companyId],
    queryFn: () => getGmailCategories(companyId),
    enabled: !!companyId && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: enabled ? 60000 : false, // refresh every minute
  });
}

// ─── Lead Integration ────────────────────────────────────────────────────────

export function useCreateLeadFromThread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, threadId, payload }) =>
      createLeadFromGmailThread({ companyId, threadId, payload }),
    onSuccess: (data, variables) => {
      toast.success(
        data.created
          ? 'New lead created and thread linked!'
          : 'Linked thread to existing lead!',
      );
      queryClient.invalidateQueries({
        queryKey: ['gmail-conversations-infinite', variables.companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['gmail-thread-messages', variables.companyId],
      });
    },
    onError: (error) => {
      const detail =
        error?.response?.data?.detail || 'Failed to create/link lead.';
      toast.error(detail);
    },
  });
}

export function useGmailLeadThreads(companyId, leadId, enabled = true) {
  return useQuery({
    queryKey: ['gmail-lead-threads', companyId, leadId],
    queryFn: () => getGmailLeadThreads({ companyId, leadId }),
    enabled: !!companyId && !!leadId && enabled,
    staleTime: 1000 * 60 * 2,
  });
}
